import React from "react";

/**
 * LAYER 2: Module Metadata Permissions
 *
 * Hook to check if the current module has permission to use HotKeys features.
 * Reads from module metadata.json and shell context.
 *
 * Expected metadata.json structure:
 * ```json
 * {
 *   "id": "documentation",
 *   "permissions": ["hotkeys:register", "hotkeys:global-scope"],
 *   "features": {
 *     "hotkeys": {
 *       "enabled": true,
 *       "scopes": ["documentation", "global"],
 *       "maxRegistrations": 50,
 *       "strictMode": true,
 *       "scopeConfig": {
 *         "global": {
 *           "enabled": true,
 *           "maxRegistrations": 5,
 *           "description": "Global shortcuts"
 *         },
 *         "documentation": {
 *           "enabled": true,
 *           "maxRegistrations": 20,
 *           "description": "Documentation-specific shortcuts"
 *         }
 *       }
 *     }
 *   }
 * }
 * ```
 */

/** Per-scope configuration options. */
export interface ScopeConfig {
  /** Whether this specific scope is enabled. */
  enabled?: boolean;
  /** Maximum registrations for this specific scope. */
  maxRegistrations?: number;
  /** Priority override for hotkeys in this scope. */
  priority?: "low" | "normal" | "high";
  /** Description of what this scope is used for. */
  description?: string;
}

export interface ModuleMetadata {
  readonly id: string;
  readonly name?: string;
  readonly permissions?: readonly (string | readonly string[])[];
  readonly features?: {
    readonly hotkeys?: {
      readonly enabled?: boolean;
      readonly scopes?: readonly string[];
      readonly maxRegistrations?: number;
      readonly priority?: "low" | "normal" | "high";
      /** When true, unauthorized registrations are blocked entirely. When false, only warnings are logged. Default: true */
      readonly strictMode?: boolean;
      /** Per-scope configuration overrides. */
      readonly scopeConfig?: {
        readonly [scopeName: string]: ScopeConfig;
      };
    };
  };
}

export interface HotkeyPermissions {
  /** Can the module register any hotkeys? */
  canRegister: boolean;
  /** Can the module use global scope shortcuts? */
  canUseGlobalScope: boolean;
  /** Maximum number of hotkeys this module can register */
  maxRegistrations: number;
  /** Scopes this module is allowed to use */
  allowedScopes: readonly string[];
  /** Module ID for logging/debugging */
  moduleId: string;
  /** When true, unauthorized registrations are blocked entirely. When false, only warnings are logged. */
  strictMode: boolean;
  /** Per-scope configuration */
  scopeConfig: { readonly [scopeName: string]: ScopeConfig };
}

/** Permissions for a specific scope. */
export interface ScopePermissions {
  /** Whether the scope is enabled for this module. */
  isEnabled: boolean;
  /** Maximum registrations allowed for this scope. */
  maxRegistrations: number;
  /** Priority level for this scope. */
  priority: "low" | "normal" | "high";
  /** Description of the scope. */
  description?: string;
  /** The scope name. */
  scopeName: string;
  /** The module ID. */
  moduleId: string;
}

/**
 * Get module metadata from window context (set by shell).
 *
 * The shell should expose this via:
 * window.__CAPITAL_RX_MODULE_METADATA__ = { ...metadata }
 */
function getModuleMetadata(): ModuleMetadata | null {
  if (typeof window === "undefined") {
    return null;
  }

  // Try to get from window context (set by shell)
  const metadata = (window as any).__CAPITAL_RX_MODULE_METADATA__;
  if (metadata) {
    return metadata;
  }

  // Fallback: Try to infer from URL path
  const path = window.location.pathname;
  const moduleMatch = path.match(/\/module\/([^/]+)/);
  if (moduleMatch) {
    return {
      id: moduleMatch[1],
      permissions: [],
    };
  }

  // No metadata available
  return null;
}

/**
 * Hook to access current module's hotkey permissions.
 *
 * @returns Permissions object with access control flags
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const permissions = useHotkeyPermissions();
 *
 *   if (!permissions.canRegister) {
 *     console.warn("Module lacks hotkey permission");
 *     return null;
 *   }
 *
 *   return <HotKey combo="CTRL+K" onTrigger={handler} />;
 * }
 * ```
 */
export function useHotkeyPermissions(): HotkeyPermissions {
  const metadata = React.useMemo(() => getModuleMetadata(), []);

  return React.useMemo(() => {
    if (!metadata) {
      // No metadata available - deny all permissions in strict mode
      return {
        canRegister: false,
        canUseGlobalScope: false,
        maxRegistrations: 0,
        allowedScopes: [],
        moduleId: "unknown",
        strictMode: true,
        scopeConfig: {},
      };
    }

    const permissions = metadata.permissions || [];
    const features = metadata.features?.hotkeys || {};

    // Check for explicit permission flags
    const hasRegisterPermission = permissions.includes("hotkeys:register");
    const hasGlobalScopePermission = permissions.includes("hotkeys:global-scope");

    // Check for feature configuration (alternative to permissions array)
    const featureEnabled = features.enabled === true;

    // Module can register if either permission exists OR feature is enabled
    const canRegister = hasRegisterPermission || featureEnabled;

    // Global scope requires explicit permission
    const canUseGlobalScope = hasGlobalScopePermission || (featureEnabled && features.scopes?.includes("global") === true);

    // Get allowed scopes (default to module ID only)
    const allowedScopes = features.scopes || [metadata.id];

    // Get max registrations (default to 25)
    const maxRegistrations = features.maxRegistrations || 25;

    // Default to strict mode unless explicitly disabled
    const strictMode = features.strictMode ?? true;

    // Get per-scope configuration (empty object if not defined)
    const scopeConfig = features.scopeConfig || {};

    return {
      canRegister,
      canUseGlobalScope,
      maxRegistrations,
      allowedScopes,
      moduleId: metadata.id,
      strictMode,
      scopeConfig,
    };
  }, [metadata]);
}

/**
 * Hook to get permissions for a specific scope.
 *
 * @param scopeName The scope to get permissions for.
 * @returns Permissions specific to the requested scope.
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const globalPerms = useScopePermissions("global");
 *   const docPerms = useScopePermissions("documentation");
 *
 *   console.log(`Global scope enabled: ${globalPerms.isEnabled}`);
 *   console.log(`Global max: ${globalPerms.maxRegistrations}`);
 * }
 * ```
 */
export function useScopePermissions(scopeName: string): ScopePermissions {
  const permissions = useHotkeyPermissions();

  return React.useMemo(() => {
    const scopeConfig = permissions.scopeConfig[scopeName] || {};

    // Check if scope is in allowed scopes list
    const isScopeAllowed = permissions.allowedScopes.includes(scopeName);

    /* Scope is enabled if it's in the allowed scopes list AND either no specific scopeConfig.enabled exists OR it's true */
    const isEnabled = isScopeAllowed && scopeConfig.enabled !== false;

    // Get scope-specific maxRegistrations, falling back to module-level default
    const maxRegistrations = scopeConfig.maxRegistrations ?? permissions.maxRegistrations;

    // Get scope-specific priority, falling back to "normal"
    const priority = scopeConfig.priority ?? "normal";

    return {
      isEnabled,
      maxRegistrations,
      priority,
      description: scopeConfig.description,
      scopeName,
      moduleId: permissions.moduleId,
    };
  }, [permissions, scopeName]);
}

/**
 * Set module metadata for current module.
 * Should be called by shell during module initialization.
 *
 * @param metadata Module metadata object
 *
 * @example
 * ```tsx
 * // In shell module loader
 * setModuleMetadata({
 *   id: "documentation",
 *   permissions: ["hotkeys:register"],
 * });
 * ```
 */
export function setModuleMetadata(metadata: ModuleMetadata): void {
  if (typeof window !== "undefined") {
    (window as any).__CAPITAL_RX_MODULE_METADATA__ = metadata;
  }
}

/**
 * Clear module metadata (for cleanup/testing).
 */
export function clearModuleMetadata(): void {
  if (typeof window !== "undefined") {
    delete (window as any).__CAPITAL_RX_MODULE_METADATA__;
  }
}
